import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import DashboardLayout from "@/components/dashboard/DashboardLayout";
import EstimateTable from "@/components/EstimateTable";
import { ArrowLeft, MapPin, Ruler, Tag } from "lucide-react";
import type { Tables } from "@/integrations/supabase/types";

type Project = Tables<"projects">;
type Estimate = Tables<"estimates">;

export default function ProjectDetails() {
  const { id } = useParams<{ id: string }>();
  const [project, setProject] = useState<Project | null>(null);
  const [estimate, setEstimate] = useState<Estimate | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!id) return;

    const fetchData = async () => {
      const { data: proj } = await supabase
        .from("projects")
        .select("*")
        .eq("id", id)
        .single();

      setProject(proj);

      const { data: est } = await supabase
        .from("estimates")
        .select("*")
        .eq("project_id", id)
        .order("created_at", { ascending: false })
        .limit(1)
        .maybeSingle();

      setEstimate(est);
      setLoading(false);
    };

    fetchData();
  }, [id]);

  if (loading) {
    return (
      <DashboardLayout>
        <div className="text-muted-foreground">Ładowanie...</div>
      </DashboardLayout>
    );
  }

  if (!project) {
    return (
      <DashboardLayout>
        <div className="text-muted-foreground">Projekt nie znaleziony.</div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      <Link
        to="/dashboard"
        className="inline-flex items-center gap-1 text-sm text-muted-foreground hover:text-foreground mb-4"
      >
        <ArrowLeft className="w-4 h-4" /> Wróć do panelu
      </Link>

      <h1 className="text-2xl font-bold tracking-tight mb-2 text-foreground">
        {project.name}
      </h1>

      <div className="flex flex-wrap items-center gap-4 text-sm text-muted-foreground mb-6">
        <span className="flex items-center gap-1">
          <MapPin className="w-4 h-4" /> {project.city}
        </span>
        <span className="flex items-center gap-1">
          <Tag className="w-4 h-4" /> {project.project_type}
        </span>
        <span className="flex items-center gap-1 font-mono">
          <Ruler className="w-4 h-4" /> {project.area_m2} m²
        </span>
        {project.client_name && (
          <span>
            Klient:{" "}
            <strong className="text-foreground">{project.client_name}</strong>
          </span>
        )}
      </div>

      <div className="border-2 border-border rounded-lg p-4 bg-card mb-6">
        <h2 className="text-xs font-bold uppercase tracking-widest text-muted-foreground mb-2">
          Opis projektu
        </h2>
        <p className="text-foreground whitespace-pre-wrap">
          {project.description}
        </p>
      </div>

      {estimate?.offer_text && (
        <div className="border-2 border-border rounded-lg p-4 bg-card mb-6">
          <h2 className="text-xs font-bold uppercase tracking-widest text-muted-foreground mb-2">
            Oferta dla klienta
          </h2>
          <p className="text-foreground whitespace-pre-wrap leading-relaxed">
            {estimate.offer_text}
          </p>
        </div>
      )}

      {estimate && (
        <div className="mb-6">
          <EstimateTable
            estimate={{
              ...estimate,
              items: Array.isArray((estimate.raw_json as any)?.items)
                ? (estimate.raw_json as any).items
                : [],
            }}
          />
        </div>
      )}

      {!estimate && (
        <div className="border-2 border-dashed border-border rounded-lg p-8 text-center text-muted-foreground">
          Brak wygenerowanej wyceny dla tego projektu.
        </div>
      )}
    </DashboardLayout>
  );
}